<template>
	<div class="content-page content-page--code-history">
		<div class="content-page__heading">
			<div class="content-page__breadcrumb-title">
				<Breadcrumbs :items="urlNames" />
				<h2 class="content-page__title">
					{{ pageTitle }}
				</h2>
			</div>
		</div>
		<div class="results-toolbar">
			<HistoryFilters
				v-model="filters"
				:defaultFilters="defaultFilters"
				:userOptions="users || []"
				placeholder="Search by property name"
			/>
			<div class="results-toolbar__counter">
				<ResultCounter :counter="resultCounter" v-if="!loading" />
			</div>
		</div>
		<Spinner :processor="{ loading: loading }" />
		<ScrollContainer v-if="!loading && hasResults" data-test="pageContent">
			<Table
				v-if="hasResults"
				:sourceObject="tableSource"
				@sort="updateDirection"
				@change="updatedPagination"
				:sortOptions="handleSort"
				class="code-history__table"
				:isArrowUp="true"
			>
				<template #body-timestamp="{ body }">
					<div>
						{{ fullDateString(body.timestamp) }}
						<!-- TODO: UI UX: add class  -->
						<div class="">
							{{ differenceDateString(body.timestamp) }}
						</div>
					</div>
				</template>
				<template #body-action="{ body }">
					<div class="code-history-log-text">
						<div v-if="body.action === 'Update'">
							<span class="code-history-log-text__bold-piece"
								>Updated
							</span>
							<span> property </span>
							<span class="code-history-log-text__bold-piece"
								>{{ body.changedPropertyName }}
							</span>
							<span>Original</span>
							<span class="code-history-log-text__bold-piece"
								>{{ body.originalValue }}
							</span>
							<span>Switched To </span>
							<span class="code-history-log-text__bold-piece"
								>{{ body.newValue }}
							</span>
						</div>
						<div v-if="body.action === 'Add'">
							<span class="code-history-log-text__bold-piece"
								>Added
							</span>
							<span>code </span>
							<span class="code-history-log-text__bold-piece">{{
								body.entityDescription
							}}</span>
						</div>
						<div v-if="body.action === 'Delete'">
							<span class="code-history-log-text__bold-piece"
								>Deleted
							</span>
							<span>code </span>
							<span class="code-history-log-text__bold-piece">{{
								body.entityDescription
							}}</span>
						</div>
					</div>
				</template>
				<template #body-revertLink="{ body }">
					<Button
						:id="body.snippetIndex.toString()"
						className="btn--link"
						labelText="Revert"
						:btnClick="() => updateAction(body.snippetIndex)"
						:disabled="body.action === 'Add'"
					/>
				</template>
			</Table>
		</ScrollContainer>

		<NoResults v-if="!hasResults && !loading" />
	</div>
</template>

<script>
import Table from '@/components/shells/table/table';
import HistoryFilters from '@/pages/global-history-screen-filters.vue';
import ResultCounter from '@/site-components/result-counter/result-counter';
import Breadcrumbs from '@/components/shells/breadcrumbs/breadcrumbs';
import LogService from '@/services/logs-service';
import Spinner from '@/components/visual/spinner/spinner';
import Button from '@/components/forms/button/button';
import NoResults from '@/site-components/no-results/no-results';
//;
import ScrollContainer from '@/components/shells/scroll-container/scroll-container';
import DateTimeMixin from '@/mixins/datetime-mixin';
import {
	MODULE_NAME,
	UPDATE_SNIPPET
} from '@/sections/forms/snippets/snippets-mutation-types';
import { mapState, mapActions } from 'vuex';

// libraries
import _ from 'lodash';
import { formatDate } from '@/utils/datetime';
import { sub } from 'date-fns';

import {
	removeObjectEmptyValues,
	removePropertiesWithSameValues
} from '@/utils/object';

export default {
	name: 'CodeHistory',
	mixins: [DateTimeMixin],
	components: {
		Table,
		ResultCounter,
		Spinner,
		Breadcrumbs,
		Button,
		HistoryFilters,
		NoResults,
		ScrollContainer
	},
	props: {
		orderBy: String,
		orderDirection: String,
		search: String,
		minDatetime: String,
		maxDatetime: String,
		changedByUserId: String,
		SourceApplication: String,
		changedEntityId: [String, Number],
		recordStart: Number,
		maxCount: Number
	},
	data() {
		return {
			loading: false,
			snippetsLogs: [],
			filters: {},
			defaultFilters: {
				orderBy: 'timestamp',
				orderDirection: 'desc',
				search: undefined,
				minDatetime: formatDate(
					new Date(sub(new Date(), { weeks: 1 }))
				),
				maxDatetime: formatDate(new Date()),
				changedByUserId: undefined,
				SourceApplication: 'VueSupport',
				changedEntityId: this.changedEntityId,
				recordStart: 1,
				maxCount: 10
			},
			snippetsHistory: [],
			urlNamesCodeManager: [
				{
					url: `/forms`,
					name: 'Global Forms'
				},
				{
					url: `/forms/code`,
					name: 'Code Manager'
				},
				{
					name: 'History'
				}
			],
			tableSource: {
				headers: [],
				displayHeader: true,
				source: {}
			}
		};
	},
	async created() {
		this.tableSource.headers = this.renderHeader();

		await this.getUsers({
			entityType: 'FormsSnippetModel',
			changedEntityId: this.changedEntityId
		});
	},
	computed: {
		...mapState('historyLogs', ['users']),
		pageTitle() {
			const pageTitle = _.get(this.tableSource, 'source.pageTitle', '');
			if (!pageTitle) {
				return 'Code Manager History';
			}
			return `Code History - ${pageTitle}`;
		},
		hasUpdatePermission() {
			return this.validatePermission('snippetsDraft.update');
		},
		hasResults() {
			return this.snippetsLogs.length > 0;
		},
		resultCounter() {
			return this.snippetsLogs.length || 0;
		},
		handleSort() {
			return {
				currentSort: this.filters.orderBy,
				currentSortDir: this.filters.orderDirection
			};
		},
		urlNames() {
			const routeBreadcrumb = this.$route.meta.breadcrumb;
			const storeBreadcrumb = this.$store.getters.breadcrumbs(
				this.$route.path
			);

			if (!_.isEmpty(routeBreadcrumb)) {
				return routeBreadcrumb;
			}

			if (!_.isEmpty(storeBreadcrumb)) {
				return storeBreadcrumb;
			}

			return this.urlNamesCodeManager;
		}
	},
	methods: {
		...mapActions(MODULE_NAME, { revertSnippet: UPDATE_SNIPPET }),
		...mapActions('historyLogs', ['getUsers']),
		async fetchData() {
			this.loading = true;
			this.loadingSource = true;
			let filteredQuery = removeObjectEmptyValues(this.filters);
			filteredQuery.entityType = 'FormsSnippetModel';

			let snippetIndex = 0;
			await LogService.GetLogs(filteredQuery)
				.then(response => {
					response.items.forEach(element => {
						if (element.action === 'Add') {
							element.result = `Added code ${element.entityDescription}`;
						}
						if (element.action === 'Update') {
							element.result = `Updated property ${element.changedPropertyName}, original ${element.originalValue} switched to ${element.newValue}`;
						}
						if (element.action === 'Delete') {
							element.result = `Deleted code with description ${element.entityDescription}`;
						}
						element.snippetIndex = snippetIndex++;
					});
					this.snippetsLogs = [...response.items];
					this.tableSource.source = response;
					if (response.httpStatus) {
						let val = {
							status: response.httpStatus,
							message: response.message
						};
						this.updateToast(val);
					}
				})
				.catch(error => {
					if (error) {
						let messageObj = {
							statusCode: 400,
							message: 'An occurred an error. Try again'
						};
						this.$store.dispatch('toastMessages/show', {
							toastId: 'snippets.history',
							statusCode: messageObj.statusCode,
							message: messageObj.message,
							slot: 'default',
							useIcon: true,
							userDismissable: true
						});
					}
				})
				.finally(() => {
					this.loading = false;
				});
		},
		renderHeader() {
			let source = [
				{
					key: 'timestamp',
					label: 'Date/Time',
					sorteable: true
				},
				{
					key: 'action',
					label: 'Action',
					sorteable: false
				},
				{
					key: 'message',
					label: 'Comments',
					sorteable: false
				},
				{
					key: 'changedByUserName',
					label: 'Changed By',
					sorteable: true
				}
			];
			if (!this.changedEntityId) {
				source.splice(
					1,
					0,
					{
						key: 'entityDescription',
						label: 'Code Description',
						sorteable: false
					},
					{
						key: 'changedEntityId',
						label: 'Code ID',
						sorteable: false
					}
				);
			}
			if (this.hasUpdatePermission) {
				source.push({
					key: 'revertLink',
					label: 'Revert Action',
					sorteable: false
				});
			}
			return source;
		},
		updateToast(val) {
			this.$store.dispatch('toastMessages/show', {
				toastId: 'snippet.action',
				statusCode: val.status,
				message: val.message,
				slot: 'default',
				useIcon: true,
				userDismissable: true
			});
		},
		async updateAction(index) {
			let snippetFromLogs = { ...this.snippetsLogs[index] };
			let snippet = {};
			snippet.id = snippetFromLogs.changedEntityId;
			snippet.description =
				snippetFromLogs.changedPropertyName === 'Description'
					? snippetFromLogs.originalValue
					: snippet.description;
			snippet.snippetAttribute =
				snippetFromLogs.changedPropertyName === 'SnippetAttribute'
					? snippetFromLogs.originalValue
					: snippet.snippetAttribute;

			snippet.snippetJustify =
				snippetFromLogs.changedPropertyName === 'SnippetJustify'
					? snippetFromLogs.originalValue
					: snippet.snippetJustify;

			snippet.snippetCalc =
				snippetFromLogs.changedPropertyName === 'SnippetCalc'
					? snippetFromLogs.originalValue
					: snippet.snippetCalc;
			snippet.snippetFormat =
				snippetFromLogs.changedPropertyName === 'SnippetFormat' &&
				snippetFromLogs.originalValue.length > 0
					? snippetFromLogs.originalValue
					: snippet.snippetFormat;

			snippet.message = `Reverted property ${snippetFromLogs.changedPropertyName} with new value ${snippetFromLogs.originalValue}`;

			await this.revertSnippet(snippet);
		},
		updateRoute(filters) {
			let queries = removePropertiesWithSameValues(
				filters,
				this.defaultFilters
			);

			queries = removeObjectEmptyValues(queries);

			let pathQuery = ['/forms', 'code', 'history'];
			if (this.changedEntityId) {
				pathQuery.splice(2, 0, this.changedEntityId);
			}
			const path = pathQuery.join('/');

			this.$router
				.push({
					path: path,
					query: queries
				})
				.catch(() => {});
		},
		updateDirection(sortValue) {
			this.filters.orderDirection = sortValue.sortDir;
			this.filters.orderBy = sortValue.sortBy;
		},
		updatedPagination(obj) {
			this.filters.recordStart = obj.page;
			this.filters.maxCount = obj.countPerPage;
		}
	},
	watch: {
		filters: {
			deep: true,
			handler(newquery) {
				this.updateRoute(newquery);
			}
		},
		'$route.query': {
			immediate: true,
			handler(newQuery) {
				if (newQuery.maxCount) {
					const availablemaxCount = [10, 20, 30, 50, 100];

					if (availablemaxCount.includes(this.maxCount)) {
						newQuery.maxCount = this.maxCount;
					} else {
						newQuery.maxCount = this.defaultFilters.maxCount;
					}
				}

				this.filters = { ...this.defaultFilters, ...newQuery };
				this.fetchData();
			}
		}
	},
	beforeRouteLeave(to, from, next) {
		if (to.name === 'code-manager') {
			const defaultBreadcrumb = [
				{ name: 'Global Forms', data: { path: '/forms' } },
				{ name: 'Code Manager', data: { path: '/forms/code' } }
			];

			const actualBreadcrumb =
				from.meta && from.meta.breadcrumb
					? [...from.meta.breadcrumb]
					: defaultBreadcrumb;

			const newBreadcrumb = actualBreadcrumb
				.filter(obj => obj.name !== 'History')
				.map(obj => {
					if (obj.name === 'Code Manager') {
						return { name: obj.name }; // Keep only name property
					}
					return obj;
				});

			to.meta.breadcrumb = newBreadcrumb;

			const obj = { path: to.fullPath, breadcrumb: newBreadcrumb };

			this.$store.commit('setBreadCrumbs', obj);
		}

		next();
	}
};
</script>

<style lang="scss" scoped>
/**
  * Global Forms Code Manager Header
**/
.code-history-log-text {
	text-transform: lowercase;

	&::v-deep .code-history-log-text__bold-piece {
		font-weight: $medium-font-weight;
	}
}
.results-toolbar {
	width: 100%;
}
::v-deep .results-toolbar__search {
	@include mq($from: md) {
		width: 250px;
	}
	@include mq($from: md-l) {
		width: 288px;
	}
}
</style>
